import { Fragment } from "react";
import { Button } from "components/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { isEmpty, omit } from "lodash";
import useMediaQuery from "components/utils/useMediaQuery";
import { ContactInboundData, ExistingContactProps } from "./types";

export const ExistingContact = ({
    contact,
    readonly,
    hiddenFields = [],
    lotLabel,
    onClear,
    onUseCustomerContact,
    customerContacts,
}: ExistingContactProps) => {
    const rowIf = (field: string, row: ContactRowProps) => (hiddenFields.includes(field) ? [] : [row]);

    const groups = [
        [
            ...rowIf("firstname", { label: "First Name", value: contact?.firstName || "-" }),
            ...rowIf("lastname", { label: "Last Name", value: contact?.lastName || "-" }),
            ...rowIf("company", { label: "Company", value: contact?.company || "-" }),
        ],
        [
            ...rowIf("address", {
                label: "Address",
                value:
                    [
                        contact?.address,
                        contact?.address_cont,
                        contact?.city,
                        [contact?.state, contact?.zip].filter((v) => !isEmpty(v)).join(" "),
                    ]
                        .filter((v) => !isEmpty(v))
                        .join(", ") || "-",
            }),
            ...rowIf("email", { label: "Email", value: contact?.email || "-" }),
            ...rowIf("phone", { label: "Phone", value: contact?.phone || "-" }),
            ...rowIf("cell", { label: "Cell", value: contact?.cell || "-" }),
            ...rowIf("fax", { label: "Fax", value: contact?.fax || "-" }),
        ],
        [
            ...rowIf("acct_number", { label: "Account Number", value: contact?.acct_number || "-" }),
            ...rowIf("taxid", { label: "Tax ID", value: contact?.taxId || "-" }),
            ...rowIf("premiseid", { label: "Premise ID", value: contact?.premiseId || "-" }),
            ...rowIf("meterid", { label: "Meter ID", value: contact?.meterId || "-" }),
            ...rowIf("lot", { label: lotLabel, value: contact?.lot || "-" }),
        ],
    ].filter((group) => group.length > 0);

    return (
        <div className="m-3">
            <div className="border rounded overflow-hidden px-3">
                <div className="border-start border-7 ps-3 ms-n3 py-3 gap-2 d-flex flex-column">
                    {groups.map((group, groupIndex) => (
                        <Fragment key={group[0].label}>
                            {groupIndex > 0 && <hr className="my-2" />}
                            {group.map((row) =>
                                row === groups[0][0] && !readonly ? (
                                    <div key={row.label} className="d-flex align-items-center gap-2">
                                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                            <ContactRow {...row} />
                                        </div>
                                        <ContactActionButton
                                            contact={contact}
                                            customerContacts={customerContacts}
                                            onClear={onClear}
                                            onUseCustomerContact={onUseCustomerContact}
                                        />
                                    </div>
                                ) : (
                                    <ContactRow key={row.label} {...row} />
                                )
                            )}
                        </Fragment>
                    ))}
                </div>
            </div>
        </div>
    );
};

type ContactRowProps = {
    label: string;
    value?: string;
    className?: string;
};

const LABEL_COLUMN_WIDTH = "180px";

const ContactRow: React.FC<ContactRowProps> = ({ label, value, className }) => {
    const isMobile = useMediaQuery("(max-width: 768px)");

    return (
        <div className={cn("d-flex", isMobile ? "flex-column" : "flex-row", className)}>
            <div className="flex-shrink-0 text-break" style={{ width: isMobile ? undefined : LABEL_COLUMN_WIDTH }}>
                {label}
            </div>
            <div className="text-gray-600 flex-grow-1 text-break" style={{ minWidth: 0 }}>
                {value || "-"}
            </div>
        </div>
    );
};

type ContactActionButtonProps = Pick<ExistingContactProps, "contact" | "customerContacts" | "onClear" | "onUseCustomerContact">;

const ContactActionButton: React.FC<ContactActionButtonProps> = ({ contact, customerContacts, onClear, onUseCustomerContact }) => {
    const isFromStoredContact = !!customerContacts?.some((c) => c.contactNumber === contact?.storedContactNumber);

    if (isFromStoredContact) {
        return (
            <Button
                aria-label="Edit contact"
                title="Edit contact"
                className="btn-icon-square flex-shrink-0"
                onClick={() => {
                    const found = customerContacts?.find((c: ContactInboundData) => c.contactNumber === contact?.storedContactNumber);
                    onUseCustomerContact?.(found ? omit(found, ["contactNumber"]) : undefined);
                }}
                variant="outline-secondary"
            >
                <FontAwesomeIcon icon={["fal", "pen-to-square"]} />
            </Button>
        );
    }

    return (
        <Button className="flex-shrink-0" variant="secondary" onClick={onClear}>
            Clear
        </Button>
    );
};
